import type { MeteringGrant } from "@/client";
import { resourceOf } from "./grantGroups";

/**
 * What to call a grant's amounts. Derived per grant rather than per section, because a
 * section can hold either resource (the onboarding grant is VIUs today, but need not be).
 */
export const unitOf = (grant: MeteringGrant) => {
	switch (resourceOf(grant)) {
		case "vius":
			return "VIUs";
		case "credits":
			return "Credits";
		default:
			return "units";
	}
};

/** The shared unit for a set of grants, or a neutral "units" when they disagree. */
export const commonUnitOf = (grants: MeteringGrant[]) => {
	const units = new Set(grants.map(unitOf));
	return units.size === 1 ? [...units][0] : "units";
};

/**
 * Grant amounts are floats and are reported verbatim — metering sources `granted`,
 * `consumed` and `remaining` independently, so they need not reconcile, and `consumed`
 * can exceed `granted`. Never round to an integer.
 */
export const formatAmount = (value: number | undefined) =>
	(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/** "600 credits left of 1,000" — remaining first, so N cannot be read as used. */
export const formatRemainingOfGranted = (
	remaining: number,
	granted: number,
	unit: string,
) => `${formatAmount(remaining)} ${unit} left of ${formatAmount(granted)}`;

/**
 * A grant with no access-schedule segments serialises as the Go zero time
 * ("0001-01-01T00:00:00Z"). Treat anything before the epoch as "no date".
 */
const parseGrantDate = (value: string | null | undefined) => {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime()) || date.getFullYear() < 1970) return null;
	return date;
};

/**
 * Grants that never expire still carry an end date, because the billing provider requires
 * one: top-ups and onboarding grants are booked a century out. An end this far away is
 * that placeholder rather than a date the customer should plan around.
 */
const PLACEHOLDER_EXPIRY_YEARS = 3;

export const isPlaceholderExpiry = (
	value: string | null | undefined,
	now: Date = new Date(),
) => {
	const date = parseGrantDate(value);
	if (!date) return false;
	const horizon = new Date(now);
	horizon.setFullYear(horizon.getFullYear() + PLACEHOLDER_EXPIRY_YEARS);
	return date > horizon;
};

export const formatGrantDate = (value: string | null | undefined) => {
	const date = parseGrantDate(value);
	if (!date) return null;
	return date.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
};

export const usageVariant = (pct: number) => {
	if (pct >= 90) return "error" as const;
	if (pct >= 75) return "warning" as const;
	return "info" as const;
};
